import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import {
  computeAnchoredListPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredListPanelBox,
} from "@/lib/viewportAnchoredPopover";

export interface ProductionCostReportPeriod {
  startDate: string;
  endDate: string;
}

interface ProductionCostReportPeriodPickerProps {
  value: ProductionCostReportPeriod;
  onChange: (next: ProductionCostReportPeriod) => void;
  previousFourWeeks: () => ProductionCostReportPeriod;
}

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"] as const;
const PANEL_GAP_PX = 6;
const VIEWPORT_EDGE_PX = 8;
const PANEL_MIN_WIDTH_PX = 300;

function parseIsoDate(value: string): Date | null {
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${String(year)}-${month}-${day}`;
}

function compareIsoDates(left: string, right: string): number {
  return left.localeCompare(right);
}

function formatCompactPeriodRange(value: ProductionCostReportPeriod): string {
  const start = parseIsoDate(value.startDate);
  const end = parseIsoDate(value.endDate);
  if (start == null || end == null) {
    return `${value.startDate} – ${value.endDate}`;
  }
  const startLabel = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const endLabel = end.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  if (start.getFullYear() === end.getFullYear()) {
    return `${startLabel} – ${endLabel}`;
  }
  const startWithYear = start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${startWithYear} – ${endLabel}`;
}

function monthMatrix(year: number, monthIndex: number): Array<{ isoDate: string; inMonth: boolean }> {
  const firstOfMonth = new Date(year, monthIndex, 1, 12, 0, 0, 0);
  const startOffset = firstOfMonth.getDay();
  const gridStart = new Date(year, monthIndex, 1 - startOffset, 12, 0, 0, 0);
  const cells: Array<{ isoDate: string; inMonth: boolean }> = [];
  for (let index = 0; index < 42; index += 1) {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + index);
    cells.push({
      isoDate: formatIsoDate(cellDate),
      inMonth: cellDate.getMonth() === monthIndex,
    });
  }
  return cells;
}

function RangeCalendarMonth(props: {
  visibleMonth: Date;
  draftStart: string;
  draftEnd: string;
  onPickDate: (isoDate: string) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
}) {
  const monthLabel = props.visibleMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const cells = monthMatrix(props.visibleMonth.getFullYear(), props.visibleMonth.getMonth());
  const rangeStart =
    props.draftStart !== "" && props.draftEnd !== ""
      ? compareIsoDates(props.draftStart, props.draftEnd) <= 0
        ? props.draftStart
        : props.draftEnd
      : props.draftStart;
  const rangeEnd =
    props.draftStart !== "" && props.draftEnd !== ""
      ? compareIsoDates(props.draftStart, props.draftEnd) <= 0
        ? props.draftEnd
        : props.draftStart
      : props.draftEnd;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={props.onPreviousMonth} aria-label="Previous month">
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <span className="text-sm font-medium text-slate-900">{monthLabel}</span>
        <Button type="button" variant="secondary" size="sm" onClick={props.onNextMonth} aria-label="Next month">
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="py-1">
            {label}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell) => {
          const inRange =
            rangeStart !== "" &&
            rangeEnd !== "" &&
            compareIsoDates(cell.isoDate, rangeStart) >= 0 &&
            compareIsoDates(cell.isoDate, rangeEnd) <= 0;
          const isStart = cell.isoDate === rangeStart;
          const isEnd = cell.isoDate === rangeEnd;
          const dayNumber = parseIsoDate(cell.isoDate)?.getDate() ?? "";
          return (
            <button
              key={cell.isoDate}
              type="button"
              onClick={() => props.onPickDate(cell.isoDate)}
              className={cn(
                "h-8 rounded-md text-sm tabular-nums transition-colors",
                !cell.inMonth && "text-slate-300",
                cell.inMonth && !inRange && "text-slate-700 hover:bg-slate-100",
                inRange && "bg-brand-50 text-brand-900",
                (isStart || isEnd) && "bg-brand-600 font-semibold text-white hover:bg-brand-700",
              )}
            >
              {dayNumber}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ProductionCostReportPeriodPicker({
  value,
  onChange,
  previousFourWeeks,
}: ProductionCostReportPeriodPickerProps) {
  const triggerId = useId();
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelBox, setPanelBox] = useState<AnchoredListPanelBox | null>(null);
  const [draftStart, setDraftStart] = useState(value.startDate);
  const [draftEnd, setDraftEnd] = useState(value.endDate);
  const [visibleMonth, setVisibleMonth] = useState(() => parseIsoDate(value.startDate) ?? new Date());

  useEffect(() => {
    if (!open) {
      setDraftStart(value.startDate);
      setDraftEnd(value.endDate);
      const parsed = parseIsoDate(value.startDate);
      if (parsed != null) {
        setVisibleMonth(parsed);
      }
    }
  }, [open, value.endDate, value.startDate]);

  const repositionPanel = () => {
    const anchor = triggerRef.current;
    if (anchor == null) {
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        gap: PANEL_GAP_PX,
        edge: VIEWPORT_EDGE_PX,
        minWidth: PANEL_MIN_WIDTH_PX,
        chromeHeight: 0,
        listMaxHeightCap: 520,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    repositionPanel();
    const onResize = () => repositionPanel();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  const canApply = draftStart !== "" && draftEnd !== "" && compareIsoDates(draftStart, draftEnd) <= 0;
  const periodLabel = useMemo(() => formatCompactPeriodRange(value), [value]);

  const pickDate = (isoDate: string) => {
    if (draftStart === "" || (draftStart !== "" && draftEnd !== "")) {
      setDraftStart(isoDate);
      setDraftEnd("");
      return;
    }
    if (compareIsoDates(isoDate, draftStart) < 0) {
      setDraftEnd(draftStart);
      setDraftStart(isoDate);
      return;
    }
    setDraftEnd(isoDate);
  };

  const applyDraft = () => {
    if (!canApply) {
      return;
    }
    onChange({ startDate: draftStart, endDate: draftEnd });
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm",
          "hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
        )}
      >
        <CalendarRange className="h-4 w-4 shrink-0 text-brand-600" aria-hidden />
        <span className="whitespace-nowrap tabular-nums">{periodLabel}</span>
      </button>

      {open && panelBox != null
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-labelledby={triggerId}
              className="rounded-lg border border-slate-200 bg-white p-4 shadow-lg"
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: Math.max(panelBox.width, PANEL_MIN_WIDTH_PX),
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
              <RangeCalendarMonth
                visibleMonth={visibleMonth}
                draftStart={draftStart}
                draftEnd={draftEnd}
                onPickDate={pickDate}
                onPreviousMonth={() =>
                  setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1, 12, 0, 0, 0))
                }
                onNextMonth={() =>
                  setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1, 12, 0, 0, 0))
                }
              />
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const next = previousFourWeeks();
                    setDraftStart(next.startDate);
                    setDraftEnd(next.endDate);
                    const parsed = parseIsoDate(next.startDate);
                    if (parsed != null) {
                      setVisibleMonth(parsed);
                    }
                  }}
                >
                  Previous 4 weeks
                </Button>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" size="sm" disabled={!canApply} onClick={applyDraft}>
                    Apply
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
